import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import "../pagesStyling/HomePage.css";
import googleLogo from "../resources/images/icons/google_logo.svg";
import jiraLogo from "../resources/images/icons/jira_logo.svg";
import confluenceLogo from "../resources/images/icons/confluence_logo.svg";
import windchillLogo from "../resources/images/icons/windchill_logo.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../components/AuthContext/AuthContext";
import { useSelectedCollections } from "../components/SelectedCollectionsContext/SelectedCollectionsContext";
import Button from "../components/Button/Button";
import SearchBar from "../components/SearchBar/SearchBar";
import moreIcon from "../resources/images/icons/dots.svg";
import { API_BASE_URL } from "../apiConfig";

interface Collection {
  uuid: string;
  name: string;
  source_origin: string;
  source_url: string;
  parent_id: string | null;
  last_sync_at: string | null;
  public_collection: boolean;
  restricted: boolean;
  has_read_access: boolean;
  has_write_access: boolean;
  access_requested: boolean;
  access_denied?: boolean;
  created_by: string;
  modified_by?: string | null;
  admin_data: any;
  embedStatus?: "Started" | "Progress" | "Completed";
  processed?: number;
  total?: number;
}

interface SelectedCollection {
  uuid: string;
  name: string;
}

function HomePage() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollectionsUuids, setSelectedCollectionsUuids] = useState<
    string[]
  >([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [username, setUsername] = useState<string>("User");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null);
  const [isSelectionMode, setIsSelectionMode] = useState<boolean>(false);
  const { token, refreshToken } = useContext(AuthContext) || {};
  const { selectedCollections, setSelectedCollections } =
    useSelectedCollections();
  const navigate = useNavigate();
  const location = useLocation();
  const newCollectionId = location.state?.newCollectionId as string | undefined;

  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const refreshAuthToken = async () => {
      if (refreshToken && token) {
        try {
          await refreshToken();
        } catch {}
      }
    };

    refreshAuthToken();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        actionsMenuRef.current &&
        !actionsMenuRef.current.contains(target) &&
        !target.closest(".more-btn")
      ) {
        setShowActionsMenu(null);
      }
    };

    if (showActionsMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActionsMenu]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/user`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const email = response.data.user || "User";
        const usernamePart = email.split("@")[0].split(".")[0];
        const formattedUsername =
          usernamePart.charAt(0).toUpperCase() + usernamePart.slice(1);
        setUsername(formattedUsername);
      } catch {
        setUsername("User");
      }
    };

    const fetchCollections = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/collections`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const result = response.data;
        const formattedData: Collection[] = result.collections.map(
          (collection: any) => ({
            uuid: collection.uuid,
            name: collection.name,
            source_origin: collection.source_origin,
            source_url: collection.source_url,
            parent_id: collection.parent_id,
            last_sync_at: collection.last_sync_at,
            public_collection: collection.public_collection,
            restricted: collection.restricted,
            has_read_access: !!collection.has_read_access,
            has_write_access: !!collection.has_write_access,
            access_requested: !!collection.access_requested,
            access_denied: !!collection.access_denied,
            created_by: collection.created_by,
            modified_by: collection.modified_by,
            admin_data: collection.admin_data,
            embedStatus: undefined,
            processed: undefined,
            total: undefined,
          })
        );
        setCollections(formattedData);

        const selectedUuidsFromContext = selectedCollections.map(
          (col: SelectedCollection) => col.uuid
        );
        setSelectedCollectionsUuids(selectedUuidsFromContext);
      } catch {}
    };

    if (token) {
      fetchUserData();
      fetchCollections();
    }

    if (
      newCollectionId &&
      !collections.some((c) => c.uuid === newCollectionId)
    ) {
      fetchCollections();
    }
  }, [token, selectedCollections, newCollectionId]);

  const getOriginIcon = (sourceOrigin: string): string => {
    switch (sourceOrigin) {
      case "GDRIVE":
        return googleLogo;
      case "JIRA":
        return jiraLogo;
      case "CONFLUENCE":
        return confluenceLogo;
      case "WINDCHILL":
        return windchillLogo;
      default:
        return googleLogo;
    }
  };

  const handleSelection = (collection: Collection): void => {
    const uuid = collection.uuid;
    let updatedSelectedUuids: string[];

    if (selectedCollectionsUuids.includes(uuid)) {
      updatedSelectedUuids = selectedCollectionsUuids.filter(
        (id) => id !== uuid
      );
    } else {
      updatedSelectedUuids = [...selectedCollectionsUuids, uuid];
    }

    setSelectedCollectionsUuids(updatedSelectedUuids);

    const updatedSelected: SelectedCollection[] = collections
      .filter((col) => updatedSelectedUuids.includes(col.uuid))
      .map((col) => ({
        uuid: col.uuid,
        name: col.name,
      }));
    setSelectedCollections(updatedSelected);
  };

  const handleDeleteClick = async (uuid: string): Promise<void> => {
    if (!token) return;

    try {
      await axios.delete(`${API_BASE_URL}/collections/single?uuid=${uuid}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setCollections((prev) => prev.filter((col) => col.uuid !== uuid));
      setSelectedCollectionsUuids((prev) => prev.filter((id) => id !== uuid));
      setSelectedCollections((prev) =>
        prev.filter((col: SelectedCollection) => col.uuid !== uuid)
      );
      setSuccessMessage("Data collection deleted successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete data collection";
      setSuccessMessage(errorMessage);
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  const handleEditClick = (collection: Collection): void => {
    navigate("/edit-data-collection", { state: { collection } });
    setShowActionsMenu(null);
  };

  const handleSyncClick = async (uuid: string): Promise<void> => {
    if (!token) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/collections/sync`,
        { uuid },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setSuccessMessage(
        response.data.message ||
          "Data collection added to sync queue successfully!"
      );
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to sync data collection";
      setSuccessMessage(errorMessage);
      setTimeout(() => setSuccessMessage(null), 3000);
    }
    setShowActionsMenu(null);
  };

  const handleIndividualChat = async (
    collection: Collection
  ): Promise<void> => {
    const updatedSelected: SelectedCollection[] = [
      {
        uuid: collection.uuid,
        name: collection.name,
      },
    ];
    setSelectedCollections(updatedSelected);
    setSelectedCollectionsUuids([collection.uuid]);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/chat/new`,
        { collections: [collection.uuid] },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const newChatId = response.data.chat_uuid;
      setSuccessMessage("New chat created successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
      navigate(`/chat/${newChatId}`, { state: { isNewChat: true } });
    } catch (error) {
      setSuccessMessage("Failed to create new chat. Please try again.");
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  const handleChatWithAllSelected = async (): Promise<void> => {
    let collectionsToUse: string[] = [];

    if (selectedCollectionsUuids.length > 0) {
      collectionsToUse = selectedCollectionsUuids;
    } else {
      collectionsToUse = collections
        .filter(
          (col) =>
            col.has_read_access &&
            (!col.embedStatus || col.embedStatus === "Completed")
        )
        .map((col) => col.uuid);
    }

    if (collectionsToUse.length === 0) {
      setSuccessMessage("No accessible collections available to start a chat.");
      setTimeout(() => setSuccessMessage(null), 3000);
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/chat/new`,
        { collections: collectionsToUse },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const newChatId = response.data.chat_uuid;
      setSuccessMessage("New chat created successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
      navigate(`/chat/${newChatId}`, { state: { isNewChat: true } });
    } catch (error) {
      setSuccessMessage("Failed to create new chat. Please try again.");
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  const handleRequestAccess = async (uuid: string): Promise<void> => {
    if (!token) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/collections/access`,
        { uuid },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setSuccessMessage(response.data.message || "Access request successful!");
      setTimeout(() => setSuccessMessage(null), 3000);

      const fetchCollections = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/collections`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          const result = response.data;
          const formattedData: Collection[] = result.collections.map(
            (collection: any) => ({
              uuid: collection.uuid,
              name: collection.name,
              source_origin: collection.source_origin,
              source_url: collection.source_url,
              parent_id: collection.parent_id,
              last_sync_at: collection.last_sync_at,
              public_collection: collection.public_collection,
              restricted: collection.restricted,
              has_read_access: !!collection.has_read_access,
              has_write_access: !!collection.has_write_access,
              access_requested: !!collection.access_requested,
              access_denied: !!collection.access_denied,
              created_by: collection.created_by,
              modified_by: collection.modified_by,
              admin_data: collection.admin_data,
              embedStatus: undefined,
              processed: undefined,
              total: undefined,
            })
          );
          setCollections(formattedData);

          const selectedUuidsFromContext = selectedCollections.map(
            (col: SelectedCollection) => col.uuid
          );
          setSelectedCollectionsUuids(selectedUuidsFromContext);
        } catch {}
      };

      fetchCollections();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to request access. Please try again.";
      setSuccessMessage(errorMessage);
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  const handleAddDataCollection = (): void => {
    navigate("/add-data-collection");
  };

  const handleSelectAll = (): void => {
    const accessibleCollections = collections
      .filter(
        (col) =>
          col.has_read_access &&
          (!col.embedStatus || col.embedStatus === "Completed")
      )
      .map((col) => col.uuid);
    setSelectedCollectionsUuids(accessibleCollections);

    const updatedSelected: SelectedCollection[] = collections
      .filter((col) => accessibleCollections.includes(col.uuid))
      .map((col) => ({
        uuid: col.uuid,
        name: col.name,
      }));
    setSelectedCollections(updatedSelected);
  };

  const handleDeselectAll = (): void => {
    setSelectedCollectionsUuids([]);
    setSelectedCollections([]);
  };

  const toggleSelectionMode = (): void => {
    setIsSelectionMode((prev) => !prev);
    if (isSelectionMode) {
      setSelectedCollectionsUuids([]);
      setSelectedCollections([]);
      setShowActionsMenu(null);
    }
  };

  const filteredCollections = collections.filter((collection) =>
    collection.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="homepage">
      <div className="main-content">
        <div className="header">
          <div className="header-welcome">
            <div className="header-text-area">
              <h1>Hi, {username}!</h1>
              <p>
                Welcome to Diabetes Care Assistant (DCA). You have available
                data collections based on your role in R&D organisation, however
                feel free to explore other data collections, just remember to
                request access.
              </p>
            </div>

            <Button
              variant="primary"
              text="Add data collection"
              onClick={handleAddDataCollection}
            />
          </div>

          <div className="controls">
            <Button
              variant="primary"
              onClick={toggleSelectionMode}
              text={isSelectionMode ? "Cancel" : "Select"}
            />
            <SearchBar
              className="homepage-search-bar"
              placeholder="Search data collections"
              onSearch={(value) => setSearchQuery(value)}
            />
          </div>
        </div>
        <div className="collections-grid">
          {filteredCollections.map((collection) => (
            <div
              key={collection.uuid}
              className={`collection-card ${
                collection.embedStatus && collection.embedStatus !== "Completed"
                  ? "greyed-out"
                  : ""
              }`}
              onClick={(e) => {
                const target = e.target as HTMLElement;
                if (
                  target.closest(".actions") ||
                  target.closest(".actions-menu")
                ) {
                  return;
                }
                if (isSelectionMode) {
                  if (
                    collection.has_read_access &&
                    (!collection.embedStatus ||
                      collection.embedStatus === "Completed")
                  ) {
                    handleSelection(collection);
                  }
                }
              }}
            >
              <div className="card-header">
                <span className="collection-type">{collection.name}</span>
                <div className="card-header-right">
                  {isSelectionMode &&
                    collection.has_read_access &&
                    (!collection.embedStatus ||
                      collection.embedStatus === "Completed") && (
                      <input
                        type="checkbox"
                        checked={selectedCollectionsUuids.includes(
                          collection.uuid
                        )}
                        onChange={() => {
                          handleSelection(collection);
                        }}
                        className="checkbox"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  {!isSelectionMode && collection.has_write_access && (
                    <div className="actions">
                      <button
                        className="more-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowActionsMenu(
                            showActionsMenu === collection.uuid
                              ? null
                              : collection.uuid
                          );
                        }}
                      >
                        <img src={moreIcon} alt="More" />
                      </button>
                      {showActionsMenu === collection.uuid && (
                        <div className="actions-menu" ref={actionsMenuRef}>
                          <button
                            className="action-menu-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(collection);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSyncClick(collection.uuid);
                            }}
                          >
                            Sync
                          </button>
                          <button
                            className="action-menu-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(collection.uuid);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="card-content">
                <div className="owner-row">
                  <span className="owner-label">Owner</span>
                  <span className="owner-name">{collection.created_by}</span>
                </div>
                <div className="origin-row">
                  <img
                    src={getOriginIcon(collection.source_origin)}
                    alt={collection.source_origin}
                    className="origin-logo active"
                  />
                </div>
              </div>
              {!isSelectionMode && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    collection.has_read_access &&
                    (!collection.embedStatus ||
                      collection.embedStatus === "Completed")
                      ? handleIndividualChat(collection)
                      : handleRequestAccess(collection.uuid)
                  }
                  disabled={
                    (collection.has_read_access &&
                      (collection.last_sync_at === null ||
                        (collection.embedStatus &&
                          collection.embedStatus !== "Completed"))) ||
                    (!collection.has_read_access && collection.access_requested)
                  }
                  text={
                    collection.has_read_access &&
                    collection.last_sync_at === null
                      ? "Data Collection Syncing"
                      : collection.has_read_access
                      ? "Start chat"
                      : collection.access_requested
                      ? "Pending request"
                      : "Request access"
                  }
                  className={
                    (collection.has_read_access &&
                      (collection.last_sync_at === null ||
                        (collection.embedStatus &&
                          collection.embedStatus !== "Completed"))) ||
                    (!collection.has_read_access && collection.access_requested)
                      ? "disabled-button"
                      : ""
                  }
                />
              )}
              {collection.embedStatus && (
                <div className="embed-status">
                  <p>Embedding Status: {collection.embedStatus}</p>
                  {collection.embedStatus === "Progress" &&
                    collection.processed !== undefined &&
                    collection.total !== undefined && (
                      <div className="progress-bar">
                        <div
                          className="progress-bar-fill"
                          style={{
                            width: `${
                              (collection.processed / collection.total) * 100
                            }%`,
                          }}
                        />
                        <span className="progress-bar-text">
                          {collection.processed} / {collection.total}
                        </span>
                      </div>
                    )}
                </div>
              )}
            </div>
          ))}
        </div>
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}
      </div>
      {isSelectionMode && (
        <div className="footer">
          <span>
            Data collections selected: {selectedCollectionsUuids.length}
          </span>
          <div className="footer-actions">
            <Button
              variant="secondary"
              text="Deselect all"
              onClick={handleDeselectAll}
            />
            <Button
              variant="secondary"
              text="Select all"
              onClick={handleSelectAll}
            />
            <Button
              variant="primary"
              text="Start chat"
              onClick={handleChatWithAllSelected}
              disabled={selectedCollectionsUuids.length === 0}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
